/* eslint-disable camelcase */
/*
 * WaWiAuftragErfassen.tsx
 * Re-Implementierung von https://huwawi.hudora.de/auftrag/neu/
 *
 * Created by Christian Klein on 2011-08-25.
 * ported by Dr. Maximillian Dornseif 2022-03-22 in huWaWi3 26.2.2
 * Copyright (c) 2011, 2014, 2017-2019, 2022, 2023 HUDORA GmbH
 */

import { useAuth0 } from '@auth0/auth0-react'
import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { Adresse, useAdrDialog } from '@hudora/hd-react-components'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { assertIsDefined, assertIsNumber, assertIsString } from 'assertate-debug'
import { useTitle } from 'fluentui-hooks'
import gql from 'graphql-tag'
import { DateTime } from 'luxon'
import * as R from 'ramda'
import React, { useCallback, useState } from 'react'
import { ResolverResult, useForm } from 'react-hook-form'
import { TextFieldElement } from 'react-hook-form-mui'
import toast from 'react-hot-toast'
import { useMutation } from 'urql'
import { navigate } from 'wouter/use-location'
import * as z from 'zod'

import { AUFTRAGSENTWURF_FRAGMENT, LIEFERADRESSE_FRAGMENT } from '../../fragments'
import { LinkRender } from '../../lib/hd-link'
import { EntitySelectComboboxSearch } from '../components/EntitySelectComboboxSearch'
import { EntitySelectComboboxStatic } from '../components/EntitySelectComboboxStatic'
import { OrderlineZufuegen } from '../components/OrderlineZufuegen'
import { AnlieferdatumInput3 } from '../Reservierungen/AnlieferdatumInput3'
import { ErfassungsPosition } from '../Reservierungen/ErfassungsPosition'

interface IPos {
  menge: number
  artnr: string
  preis: number
}

export const ZEITFENSTER_MIN = 1
export const ZEITFENSTER_MAX = 60
export const ZEITFENSTER_DEFAULT = 7

const formDefaults = {
  kundennr: '',
  anlieferdatum: DateTime.now().plus({ days: 3 }).toUTC().toISO(),
  anlieferdatum_von: DateTime.now().plus({ days: 3 }).toUTC().toISO(),
  anlieferdatum_bis: DateTime.now()
    .plus({ days: ZEITFENSTER_DEFAULT + 3 })
    .toUTC()
    .toISO(),
  zeitfenster: ZEITFENSTER_DEFAULT,
  auftragsnr_kunde: '',
  infotext_kunde: '',
  infotext_intern: '',
  infotext_logistik: '',
  lager: 'LG200',
  adrnr: '',
  positionen: [],
}

const schema = z.object({
  kundennr: z
    .string()
    .regex(/(KG|SC)[0-9]{5}/)
    .min(7),
  anlieferdatum_von: z.any(),
  zeitfenster: z.number().min(ZEITFENSTER_MIN).max(ZEITFENSTER_MAX),
  auftragsnr_kunde: z.string().max(20),
  infotext_kunde: z.string().max(1000),
  infotext_intern: z.string().max(1000),
  infotext_logistik: z.string().max(1000),
  lager: z.string().max(5),
  adrnr: z.string().max(20),
  positionen: z.any().array().nonempty().max(100),
})
const zresolver = zodResolver(schema)

export function WaWiEntwurfErfassen(props) {
  const { user } = useAuth0()
  useTitle('Auftragsentwurf erfassen')
  const theme = useTheme()
  const [adr, setAdr] = useState<Record<string, string>>({})
  const { openAdrDialog, adrDialog } = useAdrDialog(
    'Dropshipping',
    'Kostenpflichtiges Dropshipping an Einmal-Adresse zufügen.',
    adr,
    setAdr
  )
  const [positionen, setPositionen] = useState<Record<string, IPos>>({})
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'all',
    delayError: 250,
    shouldFocusError: true,
    resolver: async (values: any, context: object, options): Promise<ResolverResult> => {
      return await zresolver(values, context, options)
    },
    defaultValues: formDefaults,
  })

  // vom Input Widget <OrderlineZufuegen /> empfangene Orderline zufügen
  const onNewOrderline = useCallback(
    (menge: number, artnr: string, preis: number | undefined) => {
      assertIsString(artnr)
      assertIsNumber(menge)
      const tmpPositionen = { ...positionen }
      if (menge < 1) {
        delete tmpPositionen[artnr]
        setPositionen((prevState) => {
          const newState = { ...prevState }
          delete newState[artnr]
          return newState
        })
      } else {
        tmpPositionen[artnr] = { menge, artnr, preis }
        setPositionen((prevState) => {
          return { ...prevState, [artnr]: { menge, artnr, preis } }
        })
      }
      setValue('positionen', Object.values(tmpPositionen))
    },
    [positionen, setPositionen]
  )
  const [, mutateEntity] = useMutation(ENTWURF_CREATE_MUTATION)

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async (newInput) => {
    newInput.zeitfenster = parseInt(newInput.zeitfenster)
    assertIsNumber(newInput.zeitfenster)
    assertIsDefined(newInput.anlieferdatum)
    const result = await mutateEntity({
      input: newInput,
    } as any)
    if (result.error) {
      toast.error(`Fehler: ${result.error.message}`)
    } else {
      const newDesignator = result.data.h3AuftragsEntwurfCreate.designator
      toast.success(
        <React.Fragment>
          Erfolgreich als <LinkRender value={newDesignator} /> angelegt.
        </React.Fragment>
      )
      navigate(`/wawi/ic/e/${newDesignator}`)
    }
  }

  return (
    <section>
      <Stack spacing={theme?.spacing(1)}>
        <form
          onSubmit={handleSubmit(
            async (data: Record<string, any>, e?: Partial<Event>) =>
              await uebertragen({
                // kleine Formatanpassungen
                ...formDefaults,
                ...data,
                anlieferdatum_von: undefined,
                anlieferdatum: data?.anlieferdatum_von,
                anlieferdatum_bis: undefined,
                created_via: 'huWaWi3 WebForm',
                erfasst_von: user?.name || user?.email || user?.sub,
                dropshipping: !R.isEmpty(adr),
                dropshippingAnschrift: adr,
                positionen: Object.values(positionen).map((x) => ({ menge: x.menge, itemnr: x.artnr })),
              }),
            (errors: Object, e?: Partial<Event>) => console.log(errors)
          )}
        >
          <div>
            <div>
              <h3>Auftragsentwurf erfassen</h3>
            </div>
            <Paper variant="outlined" sx={{ p: theme?.spacing(1), m: theme?.spacing(1) }}>
              <Stack direction="row" spacing={theme?.spacing(5)}>
                <Box
                  sx={{
                    width: '40%',
                  }}
                >
                  <Stack spacing={theme?.spacing(1)}>
                    <EntitySelectComboboxSearch
                      schemaNames={['HwKunde']}
                      label="Kunde"
                      placeholder="SC12345"
                      error={!!errors.kundennr}
                      helperText={`${errors.kundennr ? errors.kundennr.message : ' '}`}
                      {...register('kundennr')}
                    />
                    <AnlieferdatumInput3
                      control={control}
                      zeitfensterMin={ZEITFENSTER_MIN}
                      zeitfensterMax={ZEITFENSTER_MAX}
                      zeitfensterDefault={ZEITFENSTER_DEFAULT}
                    />
                    {!R.isEmpty(adr) ? (
                      <Box>
                        <Adresse title={'Dropshipping'} value={adr as any} />
                      </Box>
                    ) : null}
                    {R.isEmpty(adr) ? (
                      <Box>
                        <EntitySelectComboboxStatic
                          query={LA_QUERY}
                          variables={{ nr: watch('kundennr') }}
                          label="Address№"
                          placeholder="LG200"
                          error={!!errors.adrnr}
                          helperText={`${errors.adrnr ? errors.adrnr.message : ' '}`}
                          {...register('adrnr')}
                          disabled={!watch('kundennr') || watch('kundennr')?.length < 7}
                        />
                      </Box>
                    ) : null}
                  </Stack>
                </Box>
                <Box>
                  <TextFieldElement
                    name="auftragsnr_kunde"
                    label="Auftrags№ Kunde"
                    control={control}
                    variant={'standard'}
                    fullWidth
                  />
                  <TextFieldElement
                    name="infotext_kunde"
                    label="Infotext Kunde"
                    control={control}
                    variant={'standard'}
                    multiline
                    fullWidth
                  />
                  <TextFieldElement
                    name="infotext_intern"
                    label="Infotext intern"
                    control={control}
                    variant={'standard'}
                    multiline
                    fullWidth
                  />
                  <TextFieldElement
                    control={control}
                    name="infotext_logistik"
                    label="infotext Logistik"
                    variant={'standard'}
                    multiline
                    fullWidth
                  />
                  <EntitySelectComboboxSearch
                    schemaNames={['HwLager']}
                    label="Lager"
                    placeholder="LG200"
                    error={!!errors.lager}
                    helperText={`${errors.lager ? errors.lager.message : ' '}`}
                    {...register('lager')}
                  />
                </Box>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: theme?.spacing(2), m: theme?.spacing(1) }}>
              <Stack spacing={theme?.spacing(1)}>
                <OrderlineZufuegen
                  key={Object.values(positionen).length}
                  onNewOrderline={onNewOrderline}
                  preisFeld={''}
                />
                {Object.values(positionen).length ? (
                  <Positionen poslist={Object.values(positionen)} />
                ) : (
                  <div>
                    <i>noch keine Position zugefügt</i>
                  </div>
                )}
              </Stack>
            </Paper>
          </div>
          <Stack direction="row" spacing={theme?.spacing(2)}>
            <Button
              type={'submit'}
              variant="contained"
              // disabled={!R.isEmpty(errors)}
              disabled={Object.values(positionen).length < 1 || !R.isEmpty(errors)}
            >
              Entwurf speichern
            </Button>
            <Divider orientation="vertical" flexItem />

            <Button variant="outlined" disabled={!R.isEmpty(adr)} onClick={() => openAdrDialog()}>
              Dropshipping zufügen
            </Button>
          </Stack>
        </form>
      </Stack>
      {adrDialog}
      <DevTool control={control} />
    </section>
  )
}

const ENTWURF_CREATE_MUTATION = gql`
  mutation EntwurfCreateMutation($input: H3AuftragsEntwurfInput!) {
    h3AuftragsEntwurfCreate(input: $input) {
      ...auftragsEntwurfFragment
      # positionen {
      #   itemnr
      #   menge
      #   item {
      #     id
      #     name
      #   }
      # }
    }
  }
  ${AUFTRAGSENTWURF_FRAGMENT}
`

export function Positionen({ poslist }) {
  return poslist?.length ? (
    <section>
      <table className="hd-mytable striped">
        <thead>
          <tr>
            <th>Menge</th>
            <th>Item№</th>
            <th>Artikel</th>
            <th>Liste €</th>
            <th>Auftragspreis €</th>
            <th>Wert €</th>
            <th>heute frei</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {poslist.map((pos) => (
            <ErfassungsPosition key={pos.artnr} pos={pos} />
          ))}
        </tbody>
      </table>
    </section>
  ) : null
}

const LA_QUERY = gql`
  query KundeLieferadressen($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      nodes: lieferadressen {
        ...laFragment
      }
    }
  }
  ${LIEFERADRESSE_FRAGMENT}
`
